import React from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {defineAccessControlledResource, GroupAccessRule, RestrictedPoliciesAccessRule} from '@igp/shared/access-control';
import type {KeyValueFilter, Sorting} from '@igp/shared/api';
import {useAuthUser} from '@igp/shared/auth';
import {ErrorModal} from '@igp/shared/components/error';
import {LayoutQueue, PageHeader, Toolbar, ToolbarItems, ToolbarItemsContainer} from '@igp/shared/components/layout';
import type {UserProfileFilterName, UserProfileServerFilterKeys, UserProfileViewModelKeys} from '@igp/shared/features/user-profile';
import {
    UserProfileDownloadCsvButton,
    UserProfileQueryFilters,
    useUserProfiles,
    useUserProfileSettings,
} from '@igp/shared/features/user-profile';
import {withTraceErrorBoundary} from '@igp/shared/otel';
import type {ViewType} from '@igp/shared/redux/view';
import {extendedViewCleanDelay} from '@igp/shared/redux/view';
import {EntityType} from '@models';

import {groups, useBoAgentAccessCheck} from '@access-control';
import {agentPolicy, agentResources} from '@policy-access';
import {DataGridUserProfileServer, userProfileServiceTypes} from '@features/user-profile';

const localized = defineMessages({
    title: {
        id: 'Agents_title',
        defaultMessage: 'Agents',
    },
});

function AgentsInner() {
    const {formatMessage} = useIntl();
    const viewType: ViewType = 'AgentPage';
    const isDetailsAvailable = useBoAgentAccessCheck(agentsDetailsButtonResource);
    const isAalAvailable = useBoAgentAccessCheck(aalResource);

    const columns = (
        [
            'uid_link_agent_details',
            'username',
            'nickname',
            isAalAvailable ? 'aal' : null,
            'referral.personal_referral_code',
            'copyReferralLink',
            'finance.balance',
            'finance.withdrawable_balance',
            'finance.casino_coin_balance',
            'agent_info.latest_revenue_share.poker_cash.nlh_sd',
            'agent_info.latest_revenue_share.poker_mtt',
            'agent_info.latest_revenue_share.poker_cash.plo',
            'agent_info.latest_revenue_share.casino_share',
            'agent_info.latest_revenue_share.spins',
            'agent_info.latest_revenue_share.sports',
            isDetailsAvailable ? 'details' : null,
        ] satisfies UserProfileViewModelKeys[]
    ).filter(i => i) as UserProfileViewModelKeys[];
    const defaultSorting: Sorting<UserProfileViewModelKeys>[] = [
        {
            field: 'date_of_joining.seconds',
            sort: 'desc',
        },
    ];
    const defaultFilters: KeyValueFilter<unknown, UserProfileServerFilterKeys>[] = [{key: 'isAgent', value: true}];
    const boAgentPlayerId = useAuthUser()?.playerId as string;
    const isAgent = useBoAgentAccessCheck(agentResources);

    const {
        items,
        rawItems,
        totalCount,
        searchFilter,
        filterString,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        handleFilterChange,
    } = useUserProfiles({
        viewType,
        displayName: localized.title,
        fields: columns,
        defaultPaging: {page: 1, pageSize: 25},
        defaultSorting,
        defaultFilters,
        hiddenFilters: [
            {key: 'isAgent', value: true},
            {key: 'defaultIsDownstream', value: true},
            {key: 'defaultReferrerPlayerId', value: boAgentPlayerId},
        ],
        validateFilter: () => true,
        syncWithUrl: true,
        cleanDelay: extendedViewCleanDelay,
        currency: 'USD',
    });

    const pinnedColumns: UserProfileViewModelKeys[] = ['uid_link_agent_details'];
    const {visibleColumns, onVisibleColumnsChange} = useUserProfileSettings({
        pinnedColumns,
        settingId: viewType,
        defaultColumns: columns,
    });
    const availableFilters: UserProfileFilterName[] = (
        ['agentId_nickname_username_referralCode', 'agent', isAalAvailable ? 'aal' : null] satisfies UserProfileFilterName[]
    ).filter(f => f);

    return (
        <LayoutQueue
            header={
                <PageHeader
                    header={localized.title}
                    actions={
                        <UserProfileDownloadCsvButton
                            filterString={filterString}
                            filename={formatMessage(localized.title)}
                            keys={visibleColumns}
                            mode="server"
                            downloadServerStrategyId={userProfileServiceTypes.downloadServerStrategy}
                            total={totalCount}
                        />
                    }
                />
            }
            toolbar={
                <Toolbar>
                    <ToolbarItemsContainer primary>
                        <ToolbarItems streched>
                            <UserProfileQueryFilters
                                viewMode="flex"
                                model={filterString}
                                onChange={handleFilterChange}
                                availableFilters={availableFilters}
                                isAgent={isAgent}
                                defaultPlayerId={boAgentPlayerId}
                                agentProfileEntity={EntityType.AgentProfile}
                                viewType={viewType}
                            />
                        </ToolbarItems>
                    </ToolbarItemsContainer>
                </Toolbar>
            }
            body={
                <DataGridUserProfileServer
                    pinnedColumns={pinnedColumns}
                    hasColumnSelection
                    columns={columns}
                    visibleColumns={visibleColumns}
                    onColumnsVisibilityUpdate={onVisibleColumnsChange}
                    rows={items}
                    rawModels={rawItems}
                    rowCount={totalCount}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onSortModelChange={handleSortChange}
                    page={searchFilter?.paging?.page}
                    pageSize={searchFilter?.paging?.pageSize}
                    sortModel={searchFilter?.sorting}
                />
            }
        />
    );
}

export const Agents = withTraceErrorBoundary(AgentsInner, ErrorModal);

export const agentsDetailsButtonResource = defineAccessControlledResource({
    name: 'AgentsDetailsButton',
    rules: new RestrictedPoliciesAccessRule([agentPolicy]),
});

export const aalResource = defineAccessControlledResource({
    name: 'AgentsAalFilterAndColumn',
    rules: new GroupAccessRule([groups.adminsGroup]),
});
